package com.sddamico.components.motion

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.Visibility
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.after
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private const val TARGET_ATTR = "data-underline-target"

/** Marks the element a [SlidingUnderline] should sit under. */
fun Modifier.underlineTarget(active: Boolean): Modifier =
    if (active) attrsModifier { attr(TARGET_ATTR, "") } else this

/**
 * Reserves the width of [label] in its bold weight, so a label can switch between medium and semibold without
 * nudging its neighbours sideways.
 */
@Composable
fun Modifier.stableBoldLabel(label: String): Modifier =
    this.then(StableBoldLabelStyle.toModifier()).attrsModifier { attr("data-label", label) }

val StableBoldLabelStyle = CssStyle {
    base {
        Modifier.styleModifier { property("display", "inline-flex") }.flexDirection(FlexDirection.Column)
    }
    after {
        Modifier
            .styleModifier { property("content", "attr(data-label)") }
            .fontWeight(FontWeight.SemiBold)
            .height(0.px)
            .overflow(Overflow.Hidden)
            .visibility(Visibility.Hidden)
            .userSelect(UserSelect.None)
            .pointerEvents(PointerEvents.None)
    }
}

private class UnderlinePosition(val x: Double, val y: Double, val width: Double)

/**
 * A row of items with an underline that slides to whichever child is marked with [underlineTarget]. The movement
 * ties the old selection to the new one, so switching tabs or pages reads as moving within one bar rather than
 * repainting it. With no marked child the underline fades out in place.
 */
@Composable
fun SlidingUnderline(
    activeKey: Any?,
    color: CSSColorValue,
    modifier: Modifier = Modifier,
    gap: CSSLengthNumericValue = 4.px,
    content: @Composable () -> Unit,
) {
    val reducedMotion = rememberReducedMotion()
    var container by remember { mutableStateOf<HTMLElement?>(null) }
    var position by remember { mutableStateOf<UnderlinePosition?>(null) }
    var visible by remember { mutableStateOf(false) }
    // The first placement snaps; only later changes slide.
    var placed by remember { mutableStateOf(false) }

    fun measure() {
        val target = container?.querySelector("[$TARGET_ATTR]") as? HTMLElement
        if (target == null) {
            visible = false
            return
        }
        val box = container!!.getBoundingClientRect()
        val rect = target.getBoundingClientRect()
        position = UnderlinePosition(
            x = rect.left - box.left + container!!.scrollLeft,
            y = rect.bottom - box.top + container!!.scrollTop,
            width = rect.width,
        )
        visible = true
    }

    LaunchedEffect(activeKey, container) {
        measure()
    }

    val hasPosition = position != null
    LaunchedEffect(hasPosition) {
        if (hasPosition && !placed) {
            awaitPaint()
            placed = true
        }
    }

    DisposableEffect(container) {
        val element = container ?: return@DisposableEffect onDispose { }
        // Items can change width after the first measure: web fonts load, a club badge arrives.
        val observer = ResizeObserver { _, _ -> measure() }
        observer.observe(element)
        element.children.let { children ->
            for (i in 0 until children.length) children.item(i)?.let { observer.observe(it) }
        }
        onDispose { observer.disconnect() }
    }

    Div(
        modifier
            .position(Position.Relative)
            .toAttrs {
                ref { element ->
                    container = element
                    onDispose { container = null }
                }
            }
    ) {
        content()
        val p = position
        Div(
            Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .width(1.px).height(2.px)
                .backgroundColor(color)
                .pointerEvents(PointerEvents.None)
                .styleModifier {
                    property("transform-origin", "left top")
                    if (p != null) property("transform", "translate(${p.x}px, calc(${p.y}px + $gap)) scaleX(${p.width})")
                    property("opacity", if (visible) "1" else "0")
                    if (placed && !reducedMotion) {
                        property(
                            "transition",
                            "transform ${Durations.MOVE}ms ${Ease.InOut.css}, opacity ${Durations.HOVER}ms ${Ease.Out.css}"
                        )
                    }
                }
                .toAttrs()
        )
    }
}
